import { useCallback, useEffect, useRef, useState } from "react";
import {
  type AttachedFile,
  type Message,
  estimateTokenCount,
  generateId,
  getActiveRun,
  startServerRun,
  consumeRunStream,
  cancelServerRun,
  uploadPendingFiles,
} from "@/lib/api";
import { buildContextBlock } from "@/lib/context";
import { buildApiMessages, buildSystemContent } from "@/lib/prompt-builder";
import { EMPTY_ACCUM, applyChunk, runToolEndEffect } from "@/lib/stream-reducer";
import { deriveTitle, getActiveSystemPrompt, useStore } from "@/lib/store";

type UsageInfo = { promptTokens: number; contextLength: number | null };

type Options = {
  onChatCreated?: (chatId: string) => void;
};

function keepErrorOnLastAssistant(error: string) {
  const { messages } = useStore.getState();
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant" || last.error) return;
  useStore.setState({ messages: [...messages.slice(0, -1), { ...last, error }] });
}

export function useChatRun(options: Options = {}) {
  const { onChatCreated } = options;
  const activeId = useStore((s) => s.activeId);
  const activeModel = useStore((s) => s.activeModel);
  const models = useStore((s) => s.models);
  const settings = useStore((s) => s.settings);
  const conversations = useStore((s) => s.conversations);
  const messages = useStore((s) => s.messages);
  const selectConversation = useStore((s) => s.selectConversation);
  const refreshConversations = useStore((s) => s.refreshConversations);
  const setActiveContextBlock = useStore((s) => s.setActiveContextBlock);

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessages, setStreamingMessages] = useState<Message[] | null>(null);
  const [lastUsage, setLastUsage] = useState<UsageInfo | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reattachedRef = useRef<string | null>(null);
  const streamingChatIdRef = useRef<string | null>(null);
  const detachOnlyRef = useRef(false);

  const updateLast = useCallback((patch: Partial<Message>) => {
    setStreamingMessages((prev) => {
      if (!prev) return prev;
      const updated = [...prev];
      const last = updated[updated.length - 1];
      updated[updated.length - 1] = { ...last, ...patch };
      return updated;
    });
  }, []);

  const attachToRun = useCallback(async (params: {
    runId: string;
    chatId: string;
    estimatedPromptTokens?: number;
  }) => {
    const { runId, chatId, estimatedPromptTokens } = params;
    const runAbort = new AbortController();
    abortRef.current = runAbort;
    streamingChatIdRef.current = chatId;

    let accum = EMPTY_ACCUM;
    let streamError: string | null = null;

    try {
      for await (const chunk of consumeRunStream(runId, chatId, runAbort.signal)) {
        if (runAbort.signal.aborted) break;
        const result = applyChunk(accum, chunk);
        accum = result.accum;
        if (result.patch) updateLast(result.patch);
        if (result.toolCallEnd) runToolEndEffect(result.toolCallEnd);
        if (result.stats) {
          const ctxLen = models.find((m) => m.id === result.stats!.model)?.contextLength ?? result.stats.contextLength ?? null;
          setLastUsage({ promptTokens: result.stats.promptTokens, contextLength: ctxLen });
        }
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError") && !runAbort.signal.aborted) {
        streamError = err instanceof Error ? err.message : "Something went wrong";
        updateLast({ error: streamError });
      }
    } finally {
      const detachedOnly = detachOnlyRef.current;
      detachOnlyRef.current = false;
      if (runAbort.signal.aborted && !detachedOnly) {
        void cancelServerRun(runId).catch(() => {});
      }
      abortRef.current = null;
      streamingChatIdRef.current = null;

      if (detachedOnly) {
        void refreshConversations();
        return;
      }

      setIsStreaming(false);
      await selectConversation(chatId);
      if (streamError) keepErrorOnLastAssistant(streamError);
      setStreamingMessages(null);
      void refreshConversations();

      if (!accum.finalStats && estimatedPromptTokens != null) {
        const ctxLen = models.find((m) => m.id === activeModel)?.contextLength ?? null;
        setLastUsage({ promptTokens: estimatedPromptTokens, contextLength: ctxLen });
      }
    }
  }, [activeModel, models, selectConversation, refreshConversations, updateLast]);

  useEffect(() => {
    if (streamingChatIdRef.current && streamingChatIdRef.current !== activeId) {
      detachOnlyRef.current = true;
      abortRef.current?.abort();
      setStreamingMessages(null);
      setIsStreaming(false);
    }
  }, [activeId]);

  useEffect(() => {
    if (!activeId || isStreaming) return;
    if (reattachedRef.current === activeId) return;
    if (messages.length === 0) return;
    let cancelled = false;
    (async () => {
      const info = await getActiveRun(activeId);
      if (cancelled || !info) return;
      const last = messages[messages.length - 1];
      const baseHistory = last && last.role === "assistant" ? messages.slice(0, -1) : messages;
      const placeholder: Message = last && last.role === "assistant"
        ? last
        : { id: info.assistantMessageId, role: "assistant", content: "" };
      reattachedRef.current = activeId;
      setStreamingMessages([...baseHistory, placeholder]);
      setIsStreaming(true);
      await attachToRun({ runId: info.runId, chatId: activeId });
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [activeId, isStreaming, messages, attachToRun]);

  const runCompletion = useCallback(async (pendingHistory: Message[]) => {
    const chatId = activeId ?? generateId();
    const isNewActive = !activeId;
    if (isNewActive) onChatCreated?.(chatId);

    const assistantPlaceholder: Message = { id: generateId(), role: "assistant", content: "" };
    setStreamingMessages([...pendingHistory, assistantPlaceholder]);
    setIsStreaming(true);

    let history = pendingHistory;
    const pendingUser = pendingHistory[pendingHistory.length - 1];
    try {
      const files = await uploadPendingFiles(chatId, pendingUser.files);
      if (files !== pendingUser.files) {
        history = [...pendingHistory.slice(0, -1), { ...pendingUser, files }];
        setStreamingMessages([...history, assistantPlaceholder]);
      }
    } catch (err) {
      setIsStreaming(false);
      updateLast({ error: err instanceof Error ? err.message : "Couldn't upload attachments" });
      return;
    }

    const sysPrompt = getActiveSystemPrompt().trim();
    let contextBlock = useStore.getState().activeContextBlock;
    if (!contextBlock) {
      contextBlock = buildContextBlock(settings.autoContext);
      void setActiveContextBlock(contextBlock);
    }
    const { skills: allSkills, activePinnedSkillId, activeActivatedSkillIds, artifacts, activeWorkspacePath } = useStore.getState();
    const systemContent = buildSystemContent({
      systemPrompt: sysPrompt,
      contextBlock,
      skills: allSkills,
      activePinnedSkillId,
      activeActivatedSkillIds,
      artifacts: artifacts.filter((a) => a.createdChatId === chatId),
      effort: settings.thinkingEffort,
      workspacePath: activeWorkspacePath,
    });
    const apiMessages = buildApiMessages(systemContent, history);

    const userMessage = history[history.length - 1];
    const isNewChat = !conversations.find((c) => c.id === chatId);
    const title = isNewChat ? deriveTitle(userMessage.content) : undefined;

    const estimatedPromptTokens = apiMessages.reduce((sum, m) => {
      if (typeof m.content === "string") return sum + estimateTokenCount(m.content);
      return sum + m.content.reduce((s, p) => s + estimateTokenCount(p.text ?? ""), 0) + 85;
    }, 0);

    try {
      const info = await startServerRun({
        chatId,
        userMessage: {
          id: userMessage.id,
          content: userMessage.content,
          files: userMessage.files,
          sentAt: userMessage.sentAt,
        },
        apiMessages,
        model: activeModel || undefined,
        effort: settings.thinkingEffort,
        title,
      });
      if (title) void refreshConversations();
      if (isNewActive && activeWorkspacePath) {
        const { apiFetch } = await import("@/lib/backend");
        void apiFetch(`/api/conversations/${chatId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspacePath: activeWorkspacePath }),
        }).catch(() => {});
      }
      setStreamingMessages((prev) => {
        if (!prev) return prev;
        const updated = [...prev];
        updated[updated.length - 1] = { ...updated[updated.length - 1], id: info.assistantMessageId };
        return updated;
      });
      await attachToRun({ runId: info.runId, chatId, estimatedPromptTokens });
    } catch (err) {
      setIsStreaming(false);
      updateLast({ error: err instanceof Error ? err.message : "Something went wrong" });
    }
  }, [activeId, activeModel, settings, conversations, attachToRun, refreshConversations, setActiveContextBlock, updateLast, onChatCreated]);

  const sendMessage = useCallback(async (content: string, files?: AttachedFile[]) => {
    const userMessage: Message = {
      id: generateId(),
      role: "user",
      content,
      files,
      sentAt: Date.now(),
    };
    const history = [...(streamingMessages ?? messages), userMessage];
    await runCompletion(history);
  }, [messages, streamingMessages, runCompletion]);

  const reloadLast = useCallback(async () => {
    const base = streamingMessages ?? messages;
    const lastUserIdx = base.findLastIndex((m) => m.role === "user");
    if (lastUserIdx < 0) return;
    await runCompletion(base.slice(0, lastUserIdx + 1));
  }, [messages, streamingMessages, runCompletion]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const displayMessages = streamingMessages ?? messages;

  return {
    isStreaming,
    displayMessages,
    lastUsage,
    sendMessage,
    reloadLast,
    stop,
  };
}
